import { useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Download, Loader2, Upload } from "lucide-react";
import { ApiError } from "@/lib/api";
import { Badge, Button } from "@/components/ui/primitives";
import { cn } from "@/lib/utils";
import { downloadSample, type BulkImportResult, type BulkResource } from "./api";
import { useBulkResources, useImportCsv } from "./hooks";

export default function BulkImportPage() {
  const { data: resources, isLoading } = useBulkResources();
  const [active, setActive] = useState<string | null>(null);

  // Default to the first resource the user is allowed to import.
  useEffect(() => {
    if (!active && resources && resources.length > 0) setActive(resources[0]!.key);
  }, [resources, active]);

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  if (!resources || resources.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
        You don't have permission to bulk-import any records.
      </div>
    );
  }

  const current = resources.find((r) => r.key === active) ?? resources[0]!;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Bulk upload</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Import records from a CSV file. Download the sample for a tab to see the exact
          columns, fill it in, then upload.
        </p>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-border">
        {resources.map((r) => (
          <button
            key={r.key}
            type="button"
            role="tab"
            aria-selected={current.key === r.key}
            onClick={() => setActive(r.key)}
            className={cn(
              "-mb-px rounded-t-md border-b-2 px-4 py-2 text-sm font-medium transition-colors",
              current.key === r.key
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {r.label}
          </button>
        ))}
      </div>

      <ResourcePanel key={current.key} resource={current} />
    </div>
  );
}

function ResourcePanel({ resource }: { resource: BulkResource }) {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<BulkImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const importCsv = useImportCsv();

  async function handleDownload() {
    setError(null);
    setDownloading(true);
    try {
      await downloadSample(resource.key);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not download the sample.");
    } finally {
      setDownloading(false);
    }
  }

  async function handleUpload() {
    if (!file) return;
    setError(null);
    setResult(null);
    try {
      const res = await importCsv.mutateAsync({ resource: resource.key, file });
      setResult(res);
      // Clear the picker so the same file isn't accidentally re-imported.
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "The upload failed.");
    }
  }

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">{resource.description}</p>

      {/* Columns reference */}
      <section className="rounded-lg border border-border bg-card p-5 text-card-foreground">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="text-sm font-medium">Columns</h2>
          <Button variant="outline" onClick={() => void handleDownload()} disabled={downloading}>
            {downloading ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Download className="h-4 w-4" aria-hidden="true" />
            )}
            Download sample CSV
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 pr-4 font-medium">Column</th>
                <th className="py-2 pr-4 font-medium">Required</th>
                <th className="py-2 font-medium">Notes</th>
              </tr>
            </thead>
            <tbody>
              {resource.columns.map((c) => (
                <tr key={c.name} className="border-b border-border/60 last:border-0">
                  <td className="py-2 pr-4 font-mono text-xs">{c.name}</td>
                  <td className="py-2 pr-4">
                    {c.required ? (
                      <Badge tone="warning">Required</Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">Optional</span>
                    )}
                  </td>
                  <td className="py-2 text-muted-foreground">{c.help || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Upload */}
      <section className="rounded-lg border border-border bg-card p-5 text-card-foreground">
        <h2 className="mb-3 text-sm font-medium">Upload file</h2>
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            aria-label={`CSV file for ${resource.label}`}
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              setResult(null);
              setError(null);
            }}
            className="text-sm file:mr-3 file:rounded-md file:border file:border-border file:bg-background file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-accent"
          />
          <Button onClick={() => void handleUpload()} disabled={!file || importCsv.isPending}>
            {importCsv.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Upload className="h-4 w-4" aria-hidden="true" />
            )}
            {importCsv.isPending ? "Importing…" : "Import"}
          </Button>
        </div>

        {error && (
          <div
            role="alert"
            className="mt-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </div>
        )}

        {result && <ResultView result={result} />}
      </section>
    </div>
  );
}

function ResultView({ result }: { result: BulkImportResult }) {
  const allOk = result.failed === 0;
  return (
    <div className="mt-4 space-y-3">
      <div
        className={cn(
          "flex items-center gap-2 rounded-md border px-3 py-2 text-sm",
          allOk
            ? "border-sla-ok/40 bg-sla-ok/10 text-sla-ok"
            : "border-sla-warn/40 bg-sla-warn/10 text-sla-warn",
        )}
      >
        {allOk ? (
          <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
        ) : (
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
        )}
        <span>
          <strong>{result.created}</strong> created
          {result.failed > 0 && (
            <>
              {" · "}
              <strong>{result.failed}</strong> failed
            </>
          )}{" "}
          out of {result.total} row{result.total === 1 ? "" : "s"}.
        </span>
      </div>

      {result.errors.length > 0 && (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="w-20 px-3 py-2 font-medium">Row</th>
                <th className="px-3 py-2 font-medium">Error</th>
              </tr>
            </thead>
            <tbody>
              {result.errors.map((e) => (
                <tr key={e.row} className="border-b border-border/60 last:border-0">
                  <td className="px-3 py-2 font-mono text-xs tabular-nums">{e.row}</td>
                  <td className="px-3 py-2 text-muted-foreground">{e.error}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
